import React, { useCallback, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';
import PropTypes from 'prop-types';

import Client from '@/svg-icons/client';
import AddressSelector from '../shared/AddressSelector';
import FieldErrorDisplay from '../shared/FieldErrorDisplay';

const customSelectStyles = (hasError, isDisabled) => ({
  control: (base, state) => ({
    ...base,
    minHeight: '37px',
    height: '37px',
    borderRadius: '4px',
    border: `1px solid ${hasError ? '#dc3545' : isDisabled ? '#ced4da' : state.isFocused ? '#80bdff' : '#ced4da'}`,
    boxShadow: state.isFocused && !hasError ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none',
    fontFamily: 'Open Sans',
    fontSize: '14px',
    fontWeight: 'normal',
    backgroundColor: isDisabled ? '#efefef' : 'white',
    cursor: isDisabled ? 'not-allowed' : 'default',
    '&:hover': {
      borderColor: hasError ? '#dc3545' : isDisabled ? '#ced4da' : state.isFocused ? '#80bdff' : '#ced4da',
    },
  }),
  singleValue: (base) => ({
    ...base,
    fontWeight: 'normal',
    fontSize: '14px',
    color: isDisabled ? 'rgba(0,0,0,0.38)' : '#212529',
  }),
  placeholder: base => ({
    ...base,
    fontSize: '14px',
    fontWeight: 'normal',
    color: '#b1b1b1',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    fontWeight: 'normal',
    fontFamily: 'Open Sans',
    backgroundColor: state.isSelected ? '#5882C1' : state.isFocused ? '#f0f4f9' : 'transparent',
    color: state.isSelected ? 'white' : '#374151',
    '&:active': { backgroundColor: '#5882C1' },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 12px',
    height: '100%',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    '& input': {
      height: '100% !important',
    },
  }),
  
  indicatorSeparator: () => ({ display: 'none' }),
});

const RequestInfoField = ({ disabled = false, formData, errorSchema, onChange, schema }) => {
  const safeFormData = formData || {};

  const [state, setState] = useState({
    name: safeFormData.name || '',
    document: safeFormData.document || '',
    phone: safeFormData.phone || '',
    email: safeFormData.email || '',
    client_type: safeFormData.client_type || '',
  });

  // Sync state if formData changes from outside
  useEffect(() => {
    setState({
      name: safeFormData.name || '',
      document: safeFormData.document || '',
      phone: safeFormData.phone || '',
      email: safeFormData.email || '',
      client_type: safeFormData.client_type || '',
    });
  }, [safeFormData.name, safeFormData.document, safeFormData.phone, safeFormData.email, safeFormData.client_type]);

  const errors = errorSchema || {};

  const clientType = schema?.properties?.client_type || {};
  const statesOptions = clientType?.enum
    ? clientType?.enum?.map(e => ({
        value: e,
        label: e,
      }))
    : [];

  const handlePersonChange = (field, value) => {
    const newState = { ...state, [field]: value };
    setState(newState);
    
    // Merge with existing address data in formData
    const updatedData = {
      ...safeFormData,
      ...newState,
      title: schema.title,
    };
    onChange(updatedData);
  };

  const handleAddressChange = (addressData) => {
    // Merge with existing person data in state
    const updatedData = {
      ...addressData,
      ...state,
      title: schema.title,
    };
    onChange(updatedData);
  };

  const isError = field => !!errors[field];

  const inputClasses = (field) => `
    w-full bg-white rounded-[4px] !h-[37px] !px-[12px] py-[6px] text-[14px] font-semibold font-['Open_Sans'] transition-all
    focus:border-[#80bdff] outline-none placeholder:text-gray-400
    border-[1px] border-solid ${isError(field) ? 'border-[#dc3545]' : 'border-[#ced4da]'}
    ${disabled ? 'bg-[#efefef] text-[rgba(0,0,0,0.38)] cursor-not-allowed' : 'text-[#919191]'}
  `;

  return (
    <div className="flex flex-col">
      <div className="mb-[0.6vw] mt-[0.6vw] flex items-center gap-2">
        <div className="flex items-center justify-center shrink-0 w-6 h-6 text-[#393939]">
          <Client viewBox="0 0 34.016 34.016" className="w-full h-full fill-current object-contain" />
        </div>
        <span className="font-bold text-[#212529] font-['Open_Sans'] leading-tight" style={{ fontSize: '16px', fontWeight: 700 }}>
          <FormattedMessage id="requester_info" />
        </span>
      </div>

      <div className="pl-2 sm:pl-4 md:pl-[34px] space-y-4">
        <div className="w-full">
          <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]">
            <FormattedMessage id={'client_type'} /> *
          </label>
          <Select
            id="client_type"
            options={statesOptions}
            value={state.client_type ? { value: state.client_type, label: state.client_type } : null}
            onChange={e => handlePersonChange('client_type', e?.value || '')}
            isDisabled={disabled}
            placeholder={''}
            required
            styles={customSelectStyles(isError('client_type'), disabled)}
          />
          <FieldErrorDisplay errorSchema={errors} fieldName="client_type" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
          <div className="flex flex-col">
            <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]" htmlFor="name">
              <FormattedMessage id={'name'} /> *
            </label>
            <input
              className={inputClasses('name')}
              type="text"
              id={'name'}
              value={state.name}
              onChange={event => handlePersonChange('name', event.target.value)}
              disabled={disabled}
              required
            />
            <FieldErrorDisplay errorSchema={errors} fieldName="name" />
          </div>

          <div className="flex flex-col">
            <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]" htmlFor="document">
              <FormattedMessage id={'DOCUMENT'} /> *
            </label>
            <input
              className={inputClasses('document')}
              type="text"
              id={'document'}
              value={state.document}
              onChange={event => handlePersonChange('document', event.target.value)}
              disabled={disabled}
              required
            />
            <FieldErrorDisplay errorSchema={errors} fieldName="document" />
          </div>

          <div className="flex flex-col">
            <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]" htmlFor="phone">
              <FormattedMessage id={'PHONE'} /> *
            </label>
            <input
              className={inputClasses('phone')}
              type="text"
              id={'phone'}
              value={state.phone}
              onChange={event => handlePersonChange('phone', event.target.value)}
              disabled={disabled}
              required
            />
            <FieldErrorDisplay errorSchema={errors} fieldName="phone" />
          </div>

          <div className="flex flex-col">
            <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]" htmlFor="email">
              <FormattedMessage id={'EMAIL'} /> *
            </label>
            <input
              className={inputClasses('email')}
              type="text"
              id={'email'}
              value={state.email}
              onChange={event => handlePersonChange('email', event.target.value)}
              disabled={disabled}
              required
            />
            <FieldErrorDisplay errorSchema={errors} fieldName="email" />
          </div>
        </div>

        <div className="mt-6 border-t border-gray-100 pt-6">
          <AddressSelector
            addressData={safeFormData}
            disabled={disabled}
            errors={errors}
            onChange={handleAddressChange}
            required
          />
        </div>
      </div>
    </div>
  );
};

RequestInfoField.propTypes = {
  formData: PropTypes.object,
  onChange: PropTypes.func,
  errorSchema: PropTypes.object,
  disabled: PropTypes.bool,
  schema: PropTypes.object.isRequired,
};

RequestInfoField.defaultProps = {
  formData: {},
  onChange: () => {},
  errorSchema: {},
};

export default RequestInfoField;
